<template>
    <div class="form-group row" v-if="form_type === 'search'">
        <label for="email" class="col-md-2 col-form-label text-md-right">{{ form_label }}</label>
        <div class="col-md-10">
            <v-select multiple :options="options" label="name" v-model="objects" @update:modelValue="inputs" ref="selectRef">
                <template #option="option">
                    <span v-if="option.category_name" class="tag-option-category">{{ option.category_name }}</span>
                    <span>{{ option.name }}</span>
                </template>
            </v-select>
        </div>
        <input v-for="tag in objects" type="hidden" :name="form_attribute" :value="tag.id" />
    </div>
    <div class="tag-inline-select tag-inline-grouped" v-else-if="form_type === 'search-inline'">
        <div v-for="(group, gIdx) in groupedOptions" :key="group.key" class="tag-inline-group">
            <small class="text-muted d-block mb-1">{{ group.label }}</small>
            <v-select
                multiple
                :options="group.tags"
                label="name"
                v-model="selectedByGroup[group.key]"
                @update:modelValue="onGroupChange(group.key, $event)"
                teleport="body"
            />
        </div>
        <input v-for="tag in allFormSelectedTags" :key="tag.id" type="hidden" :name="form_attribute" :value="tag.id" />
    </div>
    <div class="form-group" v-else>
        <label>{{ form_label }}</label>
        <div class="card">
            <div class="card-body p-3">
                <div v-for="(group, gIdx) in groupedOptions" :key="group.key" :class="{ 'mt-3': gIdx > 0 }">
                    <small class="text-muted d-block mb-1">{{ group.label }}</small>
                    <v-select
                        multiple
                        :options="group.tags"
                        label="name"
                        v-model="selectedByGroup[group.key]"
                        @update:modelValue="onGroupChange(group.key, $event)"
                    />
                </div>
            </div>
        </div>
        <input v-for="tag in allFormSelectedTags" :key="tag.id" type="hidden" :name="form_attribute" :value="tag.id" />
    </div>
</template>

<script>
import { ref, computed, reactive, onMounted, onBeforeUnmount } from 'vue';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import axios from 'axios';

export default {
    name: "TagAutoInputComponent",
    components: { vSelect }, // グローバル登録は不要
    props: {
        default: String,
        type: String,
        label: String,
        attribute_name: String,
        endpoint: String,
        default_id: String,
        clear_event: {
            type: String,
            default: 'clear-tags'
        }
    },
    setup(props) {
        const selected_id = ref(props.default_id ? JSON.parse(props.default_id) : []);
        const form_type = ref(props.type || 'search');
        const form_label = ref(props.label || 'タグ選択');
        const form_attribute = ref(props.attribute_name ? `${props.attribute_name}[]` : 'tags[]');
        const options = ref([]);
        const objects = ref([]);
        const selectRef = ref(null);

        // フォーム用: グループ別選択状態 { [groupKey]: [tagObject, ...] }
        const selectedByGroup = reactive({});

        // フォーム用: options を category_id でグループ化し category_sort 昇順、その他は末尾
        const groupedOptions = computed(() => {
            const map = {};
            options.value.forEach((tag) => {
                const key = tag.category_id != null ? String(tag.category_id) : 'uncategorized';
                if (!map[key]) {
                    map[key] = {
                        key,
                        label: tag.category_name || 'その他',
                        sort: tag.category_id != null ? (tag.category_sort ?? 0) : Infinity,
                        tags: [],
                    };
                }
                map[key].tags.push(tag);
            });
            return Object.values(map).sort((a, b) => a.sort - b.sort);
        });

        // フォーム用: 全グループの選択タグを結合
        const allFormSelectedTags = computed(() =>
            Object.values(selectedByGroup).flat()
        );

        // フォーム用: グループ単位の選択変更ハンドラ
        const onGroupChange = (groupKey, val) => {
            selectedByGroup[groupKey] = Array.isArray(val) ? val : [];
        };

        const inputs = (val) => {
            objects.value = Array.isArray(val) ? val : [];
        };

        const clearSelection = () => {
            objects.value = [];
            Object.keys(selectedByGroup).forEach(key => {
                selectedByGroup[key] = [];
            });
            if (selectRef.value && typeof selectRef.value.clearSelection === 'function') {
                selectRef.value.clearSelection();
            }
        };

        onMounted(async () => {
            try {
                const response = await axios.get(props.endpoint || '/api/tags');
                const sortedData = response.data.sort((a, b) => {
                    if (a.category_sort !== b.category_sort) return a.category_sort - b.category_sort;
                    return 0;
                });
                options.value = sortedData;

                const normalizedIds = selected_id.value
                    .map((id) => Number(id))
                    .filter((id) => !Number.isNaN(id));

                objects.value = options.value.filter((option) =>
                    normalizedIds.includes(Number(option.id))
                );

                // フォーム用: selectedByGroup を初期化（各グループに selected_id を振り分け）
                if (form_type.value !== 'search') {
                    groupedOptions.value.forEach((group) => {
                        selectedByGroup[group.key] = group.tags.filter((tag) =>
                            normalizedIds.includes(Number(tag.id))
                        );
                    });
                }

            } catch (error) {
                console.error('Error fetching tags:', error);
            }

            document.addEventListener(props.clear_event || 'clear-tags', clearSelection);
        });

        onBeforeUnmount(() => {
            document.removeEventListener(props.clear_event || 'clear-tags', clearSelection);
        });

        return {
            selected_id,
            form_type,
            form_label,
            form_attribute,
            options,
            inputs,
            objects,
            selectRef,
            clearSelection,
            groupedOptions,
            selectedByGroup,
            allFormSelectedTags,
            onGroupChange,
        };
    }
}
</script>

<style scoped>
.tag-option-category {
    font-size: 0.75em;
    color: #6c757d;
    margin-right: 0.25em;
}
.tag-inline-grouped {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}
.tag-inline-group {
    flex: 1 1 200px;
    min-width: 200px;
}
.tag-inline-group :deep(.vs__dropdown-toggle) {
    min-width: 0;
}
@media (max-width: 576px) {
    .tag-inline-grouped {
        flex-direction: column;
    }
    .tag-inline-group {
        min-width: 0;
    }
}
</style>
